import {
  AdGroupTargetingType,
  AdPlatform,
  APIFilterValuesForState,
  BudgetType,
  CampaignOptimizationValue,
  CampaignTargetingType,
  CampaignType,
  CompositeId,
  Goal,
  GoalType,
  TaskResponse,
  UserMaxBidAPIResponse,
} from '@app/core/models/common.models';
import { Currency } from '@app/core/models/customer.models';
import {
  CriterionAdditionCriterionType,
  CriterionAdditionMatchType,
} from '@app/client/criterion-addition/shared/models/criterion-addition.models';

/**
 * Enum for Performance Group Creation Options
 */
export enum PGCreationOptions {
  NEW = 'new-pg',
  EXISTING = 'existing-pg',
  NO = 'no-pg',
}

export enum CampaignBiddingStrategy {
  LEGACY_FOR_SALES = 'LEGACY-FOR-SALES',
  AUTO_FOR_SALES = 'AUTO-FOR-SALES',
  MANUAL = 'MANUAL',
  FIXED = 'FIXED',
  DYNAMIC = 'DYNAMIC',
}
export type PortfolioForCampaign = { portfolioName: string; portfolioId: number };

export type TargetingBids = 'closeMatch' | 'looseMatch' | 'substitutes' | 'complements';

export interface TargetExpression {
  type: TargetingPredicate;
  value?: string;

  // non-api
  label?: string;
}

export interface TargetingExpressions {
  type: TargetingExpression;
  value: string;
  bidValue: number;
  bidValueMin: number;
  bidValueMax: number;
}

export enum TargetingPredicate {
  AsinAccessoryRelated = 'ASIN-ACCESSORY-RELATED',
  AsinAgeRangeSameAs = 'ASIN-AGE-RANGE-SAME-AS',
  AsinBrandSameAs = 'ASIN-BRAND-SAME-AS',
  AsinCategorySameAs = 'ASIN-CATEGORY-SAME-AS',
  AsinExpandedFrom = 'ASIN-EXPANDED-FROM',
  AsinGenreSameAs = 'ASIN-GENRE-SAME-AS',
  AsinIsPrimeShippingEligible = 'ASIN-IS-PRIME-SHIPPING-ELIGIBLE',
  AsinPriceBetween = 'ASIN-PRICE-BETWEEN',
  AsinPriceGreaterThan = 'ASIN-PRICE-GREATER-THAN',
  AsinPriceLessThan = 'ASIN-PRICE-LESS-THAN',
  AsinReviewRatingBetween = 'ASIN-REVIEW-RATING-BETWEEN',
  AsinReviewRatingGreaterThan = 'ASIN-REVIEW-RATING-GREATER-THAN',
  AsinReviewRatingLessThan = 'ASIN-REVIEW-RATING-LESS-THAN',
  AsinSameAs = 'ASIN-SAME-AS',
  AsinSubstituteRelated = 'ASIN-SUBSTITUTE-RELATED',
  KeywordGroupSameAs = 'KEYWORD-GROUP-SAME-AS',
  QueryBroadRelMatches = 'QUERY-BROAD-REL-MATCHES',
  QueryHighRelMatches = 'QUERY-HIGH-REL-MATCHES',
}

export enum TargetingExpression {
  CloseMatch = 'CLOSEMATCH',
  Complements = 'COMPLEMENTS',
  KeywordBroadMatch = 'KEYWORDBROADMATCH',
  KeywordExactMatch = 'KEYWORDEXACTMATCH',
  KeywordGroup = 'KEYWORDGROUP',
  KeywordPhraseMatch = 'KEYWORDPHRASEMATCH',
  LooseMatch = 'LOOSEMATCH',
  PatAsin = 'PATASIN',
  PatCategory = 'PATCATEGORY',
  PatCategoryRefinement = 'PATCATEGORYREFINEMENT',
}

export enum KeywordGroup {
  Category = 'category',
}

export enum CreationLevel {
  Campaign = 'CAMPAIGN',
  AdGroup = 'ADGROUP',
  Criterion = 'CRITERION',
  Product = 'PRODUCT',
}

export interface CampaignCreationProductItem {
  productAdMutateId?: number;
  productId: string;
  id?: string;
  itemId?: string;
  bid?: number;
  title: string;
  imageUrl: string;
  asin?: string;
  sku?: string;
  price?: number;
  inStock?: boolean;
  amzRetailerOfferId?: string;
  amzRetailerId?: string;
}
interface IBidSuggestion {
  criterionMutateId?: number;
  enable: boolean;
  value: number;
}

export enum SpKeywordTheme {
  ConversionOpportunities = 'CONVERSION-OPPORTUNITIES',
  SpecialDays = 'SPECIAL-DAYS',
}

export interface AdGroupAmazonSuggestionsRequest {
  adgroupId?: number;
  campaignId?: number;
  asins?: string[];
  biddingStrategy?: CampaignBiddingStrategy;
}

export interface AdGroupAmazonBidSuggestionsRequest extends AdGroupAmazonSuggestionsRequest {
  targetingExpressions: { type: TargetingExpression; value: string }[];
}

export interface AdGroupAmazonKeywordSuggestionsRequest extends AdGroupAmazonSuggestionsRequest {
  sortBy?: SpKeywordRecommendationSortDirection;
}

export interface AdGroupAmazonKeywordGroupSuggestionsRequest {
  asins?: string[];
}

export interface AdGroupAmazonKeywordSuggestionsResponse extends TaskResponse {
  keywords: AdGroupKeywordSuggestion[];
}

export interface AdGroupAmazonKeywordGroupSuggestionsResponse extends TaskResponse {
  keywordGroups: AdGroupKeywordGroupSuggestion[];
}

export interface AdGroupAmazonProductSuggestionsResponse extends TaskResponse {
  products: AdGroupProductSuggestion[];
}

export interface AdGroupAmazonCategorySuggestionsResponse extends TaskResponse {
  categories: AdGroupCategorySuggestion[];
}

export interface AdGroupAmazonBidSuggestionsResponse extends TaskResponse {
  targetingExpressions: TargetingExpressions[];
}

export interface AdGroupAmazonBrandSearchResponse extends TaskResponse {
  brands: Brand[];
}

export interface AdGroupCategoryRefinementResponse extends TaskResponse {
  refinements: AdGroupCategoryRefinements;
}

export interface AdGroupCategoryRefinementCountResponse extends TaskResponse {
  counts: AdGroupCategorySuggestionAsinCounts;
}

export interface AdGroupSuggestedBid {
  bidMinimum: number;
  bidMedian: number;
  bidMaximum: number;
}

export interface AdGroupBidInfoSuggestion {
  matchType: CriterionAdditionMatchType;
  theme?: SpKeywordTheme;
  rank?: number;
  bid?: number;
  suggestedBid?: AdGroupSuggestedBid;

  //non-api
  selected?: boolean;
}

export interface AdGroupSearchTermSuggestionImpression {
  rank: number;
  share: number;
}

export interface AdGroupKeywordGroupSuggestion extends AdGroupBidSuggestion {
  id: KeywordGroup;
  text: string;
  sampleKeywords: string[];

  // non-api
  selected?: boolean;
}

export interface AdGroupCategorySuggestionAsinCounts {
  countMinimum: number;
  countMaximum: number;
}

export interface AdGroupCategorySuggestion extends AdGroupBidSuggestion {
  id: string;
  name: string;
  nameLocale: string;
  parentId: number;
  path: string;
  pathLocale: string;
  asinCounts: AdGroupCategorySuggestionAsinCounts;
  subCategories?: AdGroupCategorySuggestion[];

  // non-api
  selected?: boolean;
}

export interface AdGroupProductSuggestion extends AdGroupBidSuggestion {
  asin: string;
  productName: string;
  imageUrl: string;
  price: number;
  currency: Currency;
  inStock: boolean;

  // non-api
  selected?: boolean;
}

export interface AdGroupBidSuggestion {
  bid?: number;
  bidInfo?: AdGroupBidInfoSuggestion[];
  suggestedBid?: AdGroupSuggestedBid;
}

export interface AdGroupKeywordSuggestion extends AdGroupBidSuggestion {
  text: string;
  textLocale?: string;
  searchTermImpression?: AdGroupSearchTermSuggestionImpression;

  // non-api
  selected?: boolean;
}

export interface AdGroupSuggestions {
  keywords?: AdGroupKeywordSuggestion[];
  keywordGroups?: AdGroupKeywordGroupSuggestion[];
  categories?: AdGroupCategorySuggestion[];
  products?: AdGroupProductSuggestion[];
}

export interface AdGroupCategoryRefinement {
  id: string;
  name: string;
  nameLocale: string | null;
}

export interface AdGroupCategoryRefinements {
  ageRanges: AdGroupCategoryRefinement[];
  brands: AdGroupCategoryRefinement[];
  genres: AdGroupCategoryRefinement[];
}

export interface CampaignCreationAdgroup {
  adgroupMutateId: number;
  products: CampaignCreationProductItem[];
  name: string;
  defaultBid: number;
  closeMatch: IBidSuggestion;
  looseMatch: IBidSuggestion;
  substitutes: IBidSuggestion;
  complements: IBidSuggestion;
  bidSuggestions: BidSuggestions | null;
  targetBids: AdGroupTargetBid[];
  keywordBids: AdGroupKeywordBid[];
  targetingType?: AdGroupTargetingType;
  targetingTypeView?: AdGroupTargetingType;
  sortProperty?: SpKeywordRecommendationSortDirection;
  filterMatchTypes?: CriterionAdditionMatchType[];
  suggestions?: AdGroupSuggestions;
  amzRetailerId?: string;
  categorySearchTerm?: string;
  categorySearchItems?: AdGroupCategorySuggestion[];
}
export interface BidSuggestions {
  defaultBid: number;
  closeMatch: IBidSuggestion;
  looseMatch: IBidSuggestion;
  substitutes: IBidSuggestion;
  complements: IBidSuggestion;
}

export interface CampaignCreationBids {
  defaultBid: { min: number; max: number };
  targetBid: { min: number; max: number };
}

export interface IValidation {
  valid: boolean;
  tooltips: string[];
}

export interface CampaignCreationAccountInfo {
  isRas?: boolean;
  currency: Currency;
  name: string;
  platform: AdPlatform;
  accountId: string;
  adspertId: number;
  targetingType: CampaignTargetingType;
  campaignId?: number;
  campaign?: {
    name: string;
    campaignType: CampaignType;
    adgroups?: CampaignCreationAdgroup[];
  };
}
export interface CampaignInfo {
  campaignBudget: number;
  campaignEnd: Date;
  campaignName: string;
  campaignStart: Date;
  selectedPortfolio: PortfolioForCampaign;
  strategy: CampaignBiddingStrategy;
  campaignTotalBudget?: number;
  budgetType?: BudgetType;
  amzRas?: boolean;
  allowChangeDowhod: boolean;
  allowBrandTermTargeting?: boolean;
  allowComplementaryProductTargeting?: boolean;
}

export interface CampaignCreationGoalsPayload {
  currency: Currency;
  maxbid: UserMaxBidAPIResponse;
  minbid?: UserMaxBidAPIResponse;
  goals: Goal[];
  name: string;
  optimizationType: 'REVENUE';
}

export interface AdGroupCriterionBid {
  criterionMutateId?: number;
  criterionType: CriterionAdditionCriterionType;
  bid?: number;
  negative: boolean;

  suggestedBid?: AdGroupSuggestedBid;
}

export interface AdGroupTargetBid extends AdGroupCriterionBid {
  targetExpressions: TargetExpression[];

  // non-api
  suggestion?: AdGroupKeywordSuggestion | AdGroupProductSuggestion | AdGroupCategorySuggestion;
}

export interface AdGroupKeywordBid extends AdGroupCriterionBid {
  criterionName: string;
  matchType: CriterionAdditionMatchType;
  searchTermImpression?: AdGroupSearchTermSuggestionImpression;

  // non-api
  suggestion?: AdGroupKeywordSuggestion;
}

export enum SpKeywordRecommendationSortDirection {
  Clicks = 'CLICKS',
  Conversions = 'CONVERSIONS',
}

export interface QueuedAdgroup {
  retailer?: string;
  amzRetailerId?: string;
  adgroupMutateId: number;
  name: string;
  defaultMaxCpc: number;
  targetBids: AdGroupTargetBid[];
  keywordBids: AdGroupKeywordBid[];
  products: {
    productAdMutateId: number;
    sku: string;
    asin: string;
    name: string;
    itemId: string;
    bid: number;
  }[];
}

export interface QueuedCampaign {
  adspertAccountId: number;
  accountId: string;
  accountName: string;
  accountPlatform: AdPlatform;
  accountOptimize: boolean;
  accountTagsUser: any[];
  accountIsDemo: boolean;
  accountCurrency: Currency;
  campaignId?: number;
  performanceGroupId: number;
  performanceGroupName: string;
  performanceGroupOptimizationType: CampaignOptimizationValue;
  performanceGroupEnabled: boolean;
  performanceGroupGoals: {
    goalType: GoalType;
    goalValue: number;
    goalCurrency: Currency;
    goalMicros: number;
    goalRatio: any;
  }[];
  level: CreationLevel;
  adspertPortfolioId: CompositeId;
  amzPortfolioId: any;
  amzPortfolioName: string;
  campaignMutateId: number;
  name: string;
  campaignType: CampaignType;
  targetingType: CampaignTargetingType;
  biddingStrategy: CampaignBiddingStrategy;
  amzAllowChangeDowhod: boolean;
  totalBudget?: number;
  dailyBudget: number;
  budgetType: BudgetType;
  wmtAllowBrandTermTargeting: boolean;
  wmtAllowComplementaryProductTargeting: boolean;
  startDate: string;
  endDate: any;
  adgroups: QueuedAdgroup[];
}

export const CampaignFilterDefaultValue: APIFilterValuesForState = {
  apiFilters: {},
  apiSearch: {},
  filters: [],
  param: '',
};

export interface Brand {
  id: string;
  name: string;

  // non-api
  selected?: boolean;
}
